import { useEffect, useState, Fragment, useMemo } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Info } from '@phosphor-icons/react'
import ReactTooltip from 'react-tooltip'
import { useTranslation } from 'react-i18next'

import { selectActiveBuilding } from '../../reducers/buildingSelectorReducer'
import RadioField from '../FormLegacy/FormFieldsV2/RadioField'
import TextField from '../FormLegacy/FormFieldsV2/TextField'
import API from '../../api/api'

import {
  getSurveyFrequencyOptions,
  MAX_DISPLAYS_PER_DAY,
  getRecipientTypesOptions,
  renderSelectPlaceholder,
  getEmptyRecipentsPlaceholder,
  getResidentsFilterOptions,
  inAppCommunication,
  getId,
} from './helpers'
import FrequencyInputs from './FrequencyInputs'
import RecipientsFilter from './RecipientsFilter'

const FrequencyVisibility = ({
  handleChange,
  values,
  setFieldValue,
  isEdit,
  isEditingDisabled,
  frequecnyInputsDisabled,
  isAdminOrPM,
}) => {
  const { t } = useTranslation(['surveys'])
  const selectedBuilding = useSelector(selectActiveBuilding)
  const isSelectedBuildingConnectedToPMS = selectedBuilding ? selectedBuilding.PMS : false

  const [residents, setResidents] = useState({})

  const emptyRecipentsPlaceholder = getEmptyRecipentsPlaceholder(t, values.recipientType)

  const filteredRecipentTypesOptions = useMemo(() => {
    return getRecipientTypesOptions(t).filter((options) => {
      if (!isSelectedBuildingConnectedToPMS && options.value === 'units') {
        return false
      }
      return true
    })
  }, [isSelectedBuildingConnectedToPMS, t])

  const prepareRecipientsKey = (type) => {
    switch (type) {
      case 'residents':
        return 'clients'
      case 'groups':
        return 'communityGroups'
      case 'units':
        return 'unitTypes'
      default:
        return 'clientTags'
    }
  }

  const prepareValues = (key, data) => {
    switch (key) {
      case 'clients':
        return data[key].map((item) => ({
          id: item.id,
          value: item.id,
          text: item.firstName + ' ' + item.lastName,
        }))
      case 'unitTypes':
        return data[key].map(({ unitType }) => ({ id: unitType, value: unitType, text: unitType }))
      case 'communityGroups':
        return data[key].map(({ name }) => ({ id: name, value: name, text: name }))
      case 'clientTags':
        return data[key].map(({ id, name }) => ({ id, value: id, text: name }))
      default:
        return data[key]
    }
  }

  const getRecipients = async (search) => {
    const { recipientType, buildings } = values

    try {
      const buildingIds = getId(buildings)
      const { data } = await API.getSurveyRecipients(buildingIds, recipientType, search)

      const key = prepareRecipientsKey(recipientType)
      const preparedValues = prepareValues(key, data)
      setResidents((prev) => ({ ...prev, [recipientType]: preparedValues }))
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    getRecipients()
  }, [values.recipientType])

  const handleChangeVisibilityField = ({ target: { value, name } }) => {
    if (value < 1 || (name === 'visibilityPerDay' && value > MAX_DISPLAYS_PER_DAY)) return

    setFieldValue(name, value)
  }

  const handleResetRecipentFields = () => {
    ;['residents', 'groups', 'units', 'clientTags'].forEach((inputName) => {
      handleChange({
        target: {
          name: inputName,
          value: [],
        },
      })
    })
  }

  return (
    <div className="survey-form__content">
      <div className="subtitle__wrap">
        <div className="survey-form__text">{t('surveys:form.recipientsLabel')}</div>
        {filteredRecipentTypesOptions.map(({ id, value, label }) => (
          <Fragment key={id}>
            <RadioField
              type="radio"
              name={`${value}-radio`}
              additionalClass="radio-group__item survey-form__text "
              label={label}
              onChange={(e) => {
                handleResetRecipentFields()
                setFieldValue('recipientType', e.target.value)
              }}
              checked={values.recipientType === value}
              value={value}
              disabled={isEditingDisabled}
            />
            {values.recipientType === value && (
              <RecipientsFilter
                name={value}
                placeholder={renderSelectPlaceholder(t, values[value], emptyRecipentsPlaceholder)}
                options={[
                  ...(values.recipientType === 'residents'
                    ? getResidentsFilterOptions(t).filter((option) => option.value !== 'future')
                    : [
                        {
                          id: 'all',
                          text: t('surveys:recipients.allLabel', { label }),
                          selectAll: true,
                        },
                      ]),
                  ...(residents[value] || []),
                ]}
                value={values[value] || []}
                onChange={handleChange}
                disabled={isEditingDisabled}
                fetchRecipients={getRecipients}
              />
            )}
          </Fragment>
        ))}
      </div>
      <div className="subtitle__wrap">
        <div className="survey-form__text">{t('surveys:form.frequencyLabel')}</div>
        <div className="radio-group">
          {getSurveyFrequencyOptions(t).map(({ id, label, value }) => (
            <RadioField
              key={id}
              type="radio"
              name="frequency"
              additionalClass="radio-group__item survey-form__text "
              label={label}
              onChange={handleChange}
              checked={values.frequency === value}
              value={value}
              disabled={isEdit}
            />
          ))}
        </div>
      </div>
      <FrequencyInputs
        values={values}
        handleChange={handleChange}
        isEditingDisabled={frequecnyInputsDisabled}
        setFieldValue={setFieldValue}
        isAdminOrPM={isAdminOrPM}
      />
      {values.communicationType === inAppCommunication && (
        <div className="flex-row">
          <div className="survey-form__text">{t('surveys:frequencyVisibility.display')}</div>
          <TextField
            name="visibilityPerDay"
            placeholder={t('surveys:form.noDataDaysPlaceholder')}
            id="visibilityPerDay"
            type="number"
            onChange={handleChangeVisibilityField}
            value={values.visibilityPerDay || ''}
            className="survey-form__input"
            extraFieldClass="survey-form__field survey-form__field--short"
            disabled={frequecnyInputsDisabled}
          />
          <div className="survey-form__text">{t('surveys:form.displayPerDay')}</div>
          <TextField
            name="visibilityDays"
            id="visibilityDays"
            placeholder={t('surveys:form.noDataDaysPlaceholder')}
            type="number"
            onChange={handleChangeVisibilityField}
            value={values.visibilityDays || ''}
            className="survey-form__input"
            extraFieldClass="survey-form__field survey-form__field--short"
            disabled={frequecnyInputsDisabled}
          />
          <div className="survey-form__text">{t('surveys:form.daySuffix')}</div>
          <span data-for="registerTip" data-tip>
            <Info size={20} />
            <ReactTooltip
              className="survey-form__tooltip"
              id="registerTip"
              multiline
              place="top"
              effect="solid"
            >
              {t('surveys:frequencyVisibility.visibilityTip')}
            </ReactTooltip>
          </span>
        </div>
      )}
    </div>
  )
}

export default FrequencyVisibility

FrequencyVisibility.propTypes = {
  values: PropTypes.object,
  handleChange: PropTypes.func,
  setFieldValue: PropTypes.func,
  isEdit: PropTypes.bool,
  isEditingDisabled: PropTypes.bool,
  frequecnyInputsDisabled: PropTypes.bool,
  isAdminOrPM: PropTypes.bool,
  selectedBuilding: PropTypes.bool,
}
